import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import debounce from "@utils/debounce";

import { TPhoneCodeOption } from "../types";
import { createQueryEvaluationCallback } from "./createQueryEvaluationCallback";

type TPhoneCodeListSearchReturns = {
  searchOptions: TPhoneCodeOption[];
};

/**
 * @param options Phone code options
 * @returns `searchOptions` valid options for the current search
 */
export const usePhoneCodeListSearch = (options: TPhoneCodeOption[], isOpen: boolean): TPhoneCodeListSearchReturns => {
  const currentQueryRef = useRef("");
  const [searchOptions, setSearchOptions] = useState(options);
  /**
   * this function clears current query, so that on the next search we could start querying for the beginning
   * or perform a repeated query
   * @returns current search query
   */

  const clearQuery = useCallback(() => {
    currentQueryRef.current = "";
  }, []);

  const debouncedClearQuery = useMemo(() => debounce(clearQuery, 1000), [clearQuery]);

  const getAndClearCurrentQuery = useCallback(() => {
    const currentQuery = currentQueryRef.current;
    debouncedClearQuery();
    return currentQuery;
  }, [debouncedClearQuery]);

  const searchList = useCallback(() => {
    const currentQuery = getAndClearCurrentQuery();
    const filteredOptions = options.filter(createQueryEvaluationCallback(currentQuery));

    setSearchOptions(filteredOptions.length ? filteredOptions : options);
  }, [getAndClearCurrentQuery, options]);

  const debouncedSearchList = useMemo(() => debounce(searchList, 200), [searchList]);

  const handleSearchQuery = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
      }
      if (e.key.length > 1) {
        return;
      }

      currentQueryRef.current += e.key;

      debouncedSearchList();
    },
    [debouncedSearchList]
  );

  useEffect(() => {
    if (!isOpen) {
      return () => {};
    }

    window.addEventListener("keydown", handleSearchQuery);
    return () => window.removeEventListener("keydown", handleSearchQuery);
  }, [isOpen, handleSearchQuery]);

  useEffect(() => {
    if (isOpen) {
      return;
    }
    setSearchOptions(options);
  }, [isOpen, options]);

  return { searchOptions };
};
